/**
 * AddLinkDialog - Modal for adding a document link
 *
 * Uses position: fixed + useEffect click-outside (NOT createPortal per MEMORY.md).
 *
 * Features:
 * - Search input to filter project documents
 * - Document list with type badges
 * - Relation type selector (pill buttons)
 * - Excludes current document from list
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, FileText, BookOpen, StickyNote, Layout, FlaskConical } from 'lucide-react';
import type { EditorDocument, DocumentRelationType, DocumentType } from '../../api/documents';

interface AddLinkDialogProps {
  documents: EditorDocument[];
  currentDocId: string;
  onSubmit: (targetDocId: string, relationType: DocumentRelationType) => void;
  onClose: () => void;
  isCreating?: boolean;
}

const RELATION_TYPES: { value: DocumentRelationType; label: string }[] = [
  { value: 'related', label: 'Related' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'blocks', label: 'Blocks' },
  { value: 'blocked_by', label: 'Blocked By' },
  { value: 'implements', label: 'Implements' },
  { value: 'tests', label: 'Tests' },
];

function DocTypeIcon({ docType }: { docType: DocumentType }) {
  switch (docType) {
    case 'spec':
      return <BookOpen size={12} className="text-blue-500" />;
    case 'guide':
      return <Layout size={12} className="text-emerald-500" />;
    case 'note':
      return <StickyNote size={12} className="text-amber-500" />;
    case 'test':
      return <FlaskConical size={12} className="text-emerald-600" />;
    default:
      return <FileText size={12} className="text-neutral-400" />;
  }
}

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  document: 'Doc',
  spec: 'Spec',
  guide: 'Guide',
  note: 'Note',
  template: 'Template',
  test: 'Test',
};

export function AddLinkDialog({
  documents,
  currentDocId,
  onSubmit,
  onClose,
  isCreating,
}: AddLinkDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [relationType, setRelationType] = useState<DocumentRelationType>('related');
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Filter documents
  const filteredDocs = useMemo(() => {
    const q = search.toLowerCase().trim();
    return documents
      .filter((d) => d.id !== currentDocId)
      .filter((d) => !q || d.title.toLowerCase().includes(q))
      .slice(0, 20);
  }, [documents, currentDocId, search]);

  const handleSubmit = () => {
    if (!selectedDocId) return;
    onSubmit(selectedDocId, relationType);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.3)',
      }}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-xl shadow-xl border border-neutral-200 w-[420px] max-h-[520px] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
          <h3 className="text-sm font-semibold text-neutral-800">Add Link</h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-neutral-100">
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-neutral-50 rounded-lg border border-neutral-200 focus-within:border-coral-300 focus-within:ring-1 focus-within:ring-coral-100">
            <Search size={14} className="text-neutral-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
              className="flex-1 bg-transparent text-sm text-neutral-700 placeholder-neutral-400 outline-none"
            />
          </div>
        </div>

        {/* Document list */}
        <div className="flex-1 overflow-y-auto min-h-0 max-h-[240px]">
          {filteredDocs.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-xs text-neutral-400">
              No documents found
            </div>
          ) : (
            <ul className="py-1">
              {filteredDocs.map((doc) => (
                <li key={doc.id}>
                  <button
                    onClick={() => setSelectedDocId(doc.id)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors
                      ${selectedDocId === doc.id
                        ? 'bg-coral-50 text-coral-700'
                        : 'text-neutral-600 hover:bg-neutral-50'
                      }`}
                  >
                    <DocTypeIcon docType={doc.doc_type} />
                    <span className="text-xs truncate flex-1">{doc.title}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 flex-shrink-0">
                      {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Relation type selector */}
        <div className="px-4 py-2.5 border-t border-neutral-100">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5 block">
            Relation Type
          </label>
          <div className="flex flex-wrap gap-1.5">
            {RELATION_TYPES.map((rt) => (
              <button
                key={rt.value}
                onClick={() => setRelationType(rt.value)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors
                  ${relationType === rt.value
                    ? 'bg-coral-100 text-coral-700 border border-coral-200'
                    : 'bg-neutral-50 text-neutral-500 border border-neutral-200 hover:border-neutral-300'
                  }`}
              >
                {rt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="px-4 py-3 border-t border-neutral-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedDocId || isCreating}
            className="px-3 py-1.5 text-xs font-medium text-white bg-coral-500 hover:bg-coral-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {isCreating ? 'Adding...' : 'Add Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddLinkDialog;
